import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion /
// mood_tracker / oriora_pawline). askBrain sends the message + this app's declared action
// catalog to the generic fleet chat brain (POST /api/apps/oriora_garage/chat) — ONE billed call
// returns ONE registered action + a reply; the app executes it locally so vehicle data stays
// on-device. The regex intent-router below (routeMessage) is the FREE FALLBACK (offline /
// signed-out / out of funds) — chat never goes dead. Original prototype notes:
//   • "add vehicle …"                              → light client intent (local)   → add_vehicle
//   • "add/log fillup … <odo> <vol> [$cost]"        → local number/unit extraction  → add_fillup
//   • "add/log service <kind> at <odo> [$cost]"     → local number/unit extraction  → add_service
//   • "summar… / overview / recap"                  → existing /narrate endpoint    → summarize
//   • "how much … / when did … / what's my … ?"     → existing /ask endpoint        → ask
//   • "show / find / list fillups|service …"        → local list + filter (free)    → filter
//   • "delete / remove <last|term> fillup|service"  → resolve locally               → delete_*
//   • "set/change/mark <last> fillup|service …"     → resolve locally               → edit_*
//   • anything else                                 → none + a short clarifying nudge
//
// Everything except the two AI actions (summarize/ask) executes locally. Those two reuse the
// SAME two endpoints ("Garage recap" / "Ask your garage" cards already use) — no new server
// route, no new prompt, still ONE billed call each, still observational-only (never advice,
// never "due"/"overdue", never diagnosis — enforced server-side, unchanged).
//
// Chat operates on the SAME active vehicle the screen has selected (ChatContext), exactly like
// before this migration — the model never invents/resolves a vehicle id, that stays local.

import {
  store, normalizeKind, prettyKind, SERVICE_KINDS,
  type Fillup, type Service, type FillupPatch, type ServicePatch, type Vehicle, type VehiclePatch,
} from "../store";
import { BrainError, brainCall, isCarAdviceQuestion, CAR_ADVICE_DECLINED } from "../brain";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; the executor supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage/routeEdit build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope`. Neutral vehicle log — but car maintenance touches
// safety, so the scope keeps the reply channel observational + declines repair/safety diagnosis.
const SCOPE =
  "This app is a private vehicle log: add/edit/delete/search vehicles, fill-ups and service records, and give " +
  "an OBSERVATIONAL recap/answer about the user's OWN logged data (MPG, costs, service intervals, totals). " +
  "Describe their logged data; never give mechanical-repair, safety, or diagnostic advice and never say whether " +
  "a part needs replacing — point to a qualified mechanic. Back up to their own cloud.";

// The active vehicle context — chat operates on the SAME vehicle the screen has selected,
// exactly like the "Garage recap" / "Ask your garage" cards do.
export interface ChatContext {
  vehicleId: string | null;
  vehicleName: string | null;
}

const HELP =
  'I can log fill-ups and service, add a vehicle, find entries, or narrate your numbers — just tell me. Try:\n' +
  '• "add fillup 42350 mi, 12.4 gal, $46.80"\n' +
  '• "log service oil change at 42000 mi, $60"\n' +
  '• "summarize this car"\n' +
  '• "how much has this car cost me this year?"\n' +
  '• "show fillups over $50" · "delete last fillup"';

const NO_VEHICLE_HELP = 'Add a vehicle first — try "add vehicle My Car" — then log fill-ups and service.';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

function parseNum(s: string): number | null {
  const n = parseFloat(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** First $-tagged or "N dollars" amount in the text. */
function extractCost(s: string): number | null {
  let m = s.match(/\$\s*([\d,]+(?:\.\d+)?)/);
  if (!m) m = s.match(/([\d,]+(?:\.\d+)?)\s*(?:dollars|usd)\b/i);
  return m ? parseNum(m[1]) : null;
}
/** First number immediately preceded by one of the given keyword tags (e.g. "odo 42000"). */
function extractTagged(s: string, tags: string[]): number | null {
  const re = new RegExp(`(?:${tags.join("|")})\\s*[:\\-]?\\s*([\\d,]+(?:\\.\\d+)?)`, "i");
  const m = s.match(re);
  return m ? parseNum(m[1]) : null;
}
/** First number immediately followed by one of the given units (e.g. "12.4 gal"). */
function extractUnitNumber(s: string, units: string[]): number | null {
  const re = new RegExp(`([\\d,]+(?:\\.\\d+)?)\\s*(?:${units.join("|")})\\b`, "i");
  const m = s.match(re);
  return m ? parseNum(m[1]) : null;
}

const ODO_TAGS = ["odo", "odometer"];
const ODO_UNITS = ["mi", "miles", "km", "kilometers", "kilometres"];
const VOL_TAGS = ["volume", "vol"];
const VOL_UNITS = ["gal", "gallons", "l", "liters", "litres"];

// Fuzzy resolution (fleet standard, fuzzy.ts): when exact substring misses, fall back to a
// forgiving NEAR-match so "delete the oil chang" resolves a stored "oil-change" and a mistyped
// note still finds its fill-up. All client-side — data lives on-device; the model never resolves an id.
const LAST_RE = /^(last|latest|recent|most\s+recent)$/;
const fillupName = (f: Fillup): string => f.note;
const serviceName = (s: Service): string => `${prettyKind(s.kind)} ${s.note}`.trim();

async function findFillup(vehicleId: string, term: string): Promise<Fillup[]> {
  const all = await store.listFillups(vehicleId, 50000);
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (LAST_RE.test(q) || !q) {
    return all.length ? [all[0]] : []; // listFillups is already newest-first (see db.rs)
  }
  const subs = all.filter((f) => f.note.toLowerCase().includes(q));
  if (subs.length) return subs;
  const named = all.map((f) => ({ name: fillupName(f), f }));
  return findNamedMatches(named, term).map((n) => n.f);
}
async function findService(vehicleId: string, term: string): Promise<Service[]> {
  const all = await store.listServices(vehicleId, 50000);
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (LAST_RE.test(q) || !q) {
    return all.length ? [all[0]] : [];
  }
  const subs = all.filter((s) => s.kind.toLowerCase().includes(q) || s.note.toLowerCase().includes(q));
  if (subs.length) return subs;
  const named = all.map((s) => ({ name: serviceName(s), s }));
  return findNamedMatches(named, term).map((n) => n.s);
}
// A helpful "did you mean …?" when a NAMED term matched nothing (skips "last"/blank — nothing to suggest).
async function fillupNotFound(vehicleId: string, term: string): Promise<string> {
  const q = term.trim();
  if (!q || LAST_RE.test(q.toLowerCase())) return "I couldn't find that fill-up.";
  const all = await store.listFillups(vehicleId, 50000);
  return notFoundMessage(all.map((f) => ({ name: fillupName(f) })), q);
}
async function serviceNotFound(vehicleId: string, term: string): Promise<string> {
  const q = term.trim();
  if (!q || LAST_RE.test(q.toLowerCase())) return "I couldn't find that service entry.";
  const all = await store.listServices(vehicleId, 50000);
  return notFoundMessage(all.map((s) => ({ name: serviceName(s) })), q);
}
function pickFillupList(rows: Fillup[]): string {
  return rows.slice(0, 5).map((f) => `• ${f.ts.slice(0, 10)} — ${f.volume} at ${f.odometer}`).join("\n");
}
function pickServiceList(rows: Service[]): string {
  return rows.slice(0, 5).map((s) => `• ${s.ts.slice(0, 10)} — ${prettyKind(s.kind)} at ${s.odometer}`).join("\n");
}

export async function askBrain(history: ChatTurn[], ctx: ChatContext, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text, ctx); return { reply: r.reply, actions: [r.action] }; }
  // THE FENCE — pre-LLM, free, on BOTH paths (workout_tracker's shape): an advice/due/diagnosis/
  // price-judgment ask is refused BEFORE the billed server call, and a network failure must never
  // open the gate to it reaching the model via the local fallback's billed ask action.
  if (isCarAdviceQuestion(text)) return { reply: "", actions: [none(CAR_ADVICE_DECLINED).action] };
  try {
    return await serverChat(history, text, ctx, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text, ctx);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (fill-up/service "last"/term text → id) locally,
// always scoped to the ACTIVE vehicle (ctx.vehicleId) — the model never assigns a vehicle id.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.garage.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_vehicle", description: "Add a new vehicle to the garage.", params: { name: "REQUIRED — the vehicle's name", make: "optional make", model: "optional model", year: "optional model year, integer" } },
  { name: "edit_vehicle", description: "Change a vehicle's details — name, make, model, year, or plate. Give at least one field. Defaults to the ACTIVE vehicle; name another to edit it.", params: { target: "optional — which vehicle by name; omit for the active one", name: "new name, only if renaming", make: "new make, only if changing", model: "new model, only if changing", year: "new model year (integer), only if changing", plate: "new plate, only if changing" } },
  { name: "delete_vehicle", description: "Delete a whole vehicle AND all of its fill-ups and service records (the app asks the user to confirm first). Defaults to the ACTIVE vehicle; name another to delete it.", params: { target: "optional — which vehicle by name; omit for the active one" } },
  { name: "add_fillup", description: "Log a fuel fill-up for the ACTIVE vehicle.", params: { odometer: "REQUIRED — odometer reading, a number", volume: "REQUIRED — fuel volume, a number", cost: "optional total cost paid, a number", is_full_tank: "optional true/false — defaults true; false means a partial fill" } },
  { name: "add_service", description: "Log a service/maintenance event for the ACTIVE vehicle.", params: { kind_name: "REQUIRED — what was done, e.g. oil change, brakes, tires", odometer: "REQUIRED — odometer reading, a number", cost: "optional total cost paid, a number" } },
  { name: "edit_fillup", description: "Correct a logged fill-up (cost, odometer, or full/partial). Give at least one field to change.", params: { target: 'which fill-up — "last"/"latest", or a distinctive word from its note; defaults to the last one', cost: "new cost, only if changing", odometer: "new odometer, only if changing", is_full_tank: "true/false, only if changing full vs partial" } },
  { name: "edit_service", description: "Correct a logged service entry (cost, odometer, or kind). Give at least one field to change.", params: { target: 'which service — "last"/"latest", or a distinctive word from its kind/note; defaults to the last one', cost: "new cost, only if changing", odometer: "new odometer, only if changing", kind_name: "new kind/category, only if changing" } },
  { name: "delete_fillup", description: "Delete one logged fill-up (the app asks the user to confirm first).", params: { target: 'which fill-up — "last"/"latest", or a distinctive word from its note; defaults to the last one' } },
  { name: "delete_service", description: "Delete one logged service entry (the app asks the user to confirm first).", params: { target: 'which service — "last"/"latest", or a distinctive word from its kind/note; defaults to the last one' } },
  { name: "filter", description: "Search / list this vehicle's fill-ups and/or service entries.", params: { entity: 'optional "fillup", "service", or "both" — defaults to both', term: "optional free text to match against notes/kind", min_cost: "optional minimum cost, a number", max_cost: "optional maximum cost, a number" } },
  { name: "summarize", description: "One of the TWO AI-billed features: narrate the ACTIVE vehicle's computed figures (economy, cost-per-distance, service intervals). Observational only — never says a service is due/overdue, never diagnoses, never financial advice.", params: {} },
  { name: "ask", description: "The other AI-billed feature: answer a question about the ACTIVE vehicle from its computed figures (e.g. \"how much did this car cost me this year?\"). Observational only — states what the numbers support, says so if the data doesn't cover it, never advises.", params: { question: "REQUIRED — the user's question, copied verbatim" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole vehicle log — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

function buildContext(ctx: ChatContext): string {
  const parts = [ctx.vehicleId ? `Active vehicle: ${ctx.vehicleName ?? "(unnamed)"}.` : "No vehicle selected yet."];
  parts.push(`Service kinds in use: ${SERVICE_KINDS.join(", ")}.`);
  return parts.join(" ");
}

async function serverChat(history: ChatTurn[], text: string, ctx: ChatContext, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = buildContext(ctx) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, ctx);
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asNum = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};
const asBool = (v: unknown): boolean | null => {
  if (typeof v === "boolean") return v;
  const s = asStr(v)?.toLowerCase();
  if (s === "true" || s === "full" || s === "yes") return true;
  if (s === "false" || s === "partial" || s === "no") return false;
  return null;
};

export async function toAction(r: ServerChat, ctx: ChatContext): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }

  if (r.action === "add_vehicle") {
    const name = asStr(p.name);
    if (!name) return none(say || "What should the vehicle be called?");
    return { reply: say, action: { kind: "add_vehicle", name, make: asStr(p.make), model: asStr(p.model), year: asNum(p.year) ?? undefined } };
  }
  // Vehicle edit/delete are vehicle-LEVEL (handled before the active-vehicle guard): they can target
  // the active vehicle by default, or any vehicle by name. The resolver's outcome always beats the
  // model's claim (§2.3), so a miss shows the problem, never the model's "done".
  if (r.action === "edit_vehicle") {
    const res = await resolveVehicle(asStr(p.target), ctx);
    if (res.problem) return none(res.problem);
    const patch: VehiclePatch = {};
    const bits: string[] = [];
    const nm = asStr(p.name); if (nm) { patch.name = nm; bits.push(`name → ${nm}`); }
    const make = asStr(p.make); if (make) { patch.make = make; bits.push(`make → ${make}`); }
    const model = asStr(p.model); if (model) { patch.model = model; bits.push(`model → ${model}`); }
    const year = asNum(p.year); if (year !== null) { patch.year = year; bits.push(`year → ${year}`); }
    const plate = asStr(p.plate); if (plate) { patch.plate = plate; bits.push("plate updated"); }
    if (bits.length === 0) return none(say || "What should I change on that vehicle — name, make, model, year, or plate?");
    return { reply: say, action: { kind: "edit_vehicle", id: res.vehicle!.id, patch, label: res.vehicle!.name } };
  }
  if (r.action === "delete_vehicle") {
    const res = await resolveVehicle(asStr(p.target), ctx);
    if (res.problem) return none(res.problem);
    return { reply: say, action: { kind: "delete_vehicle", id: res.vehicle!.id, label: res.vehicle!.name } };
  }
  if (r.action === "backup_save") return { reply: say, action: { kind: "backup_save" } };
  if (r.action === "backup_status") return { reply: say, action: { kind: "backup_status" } };
  if (r.action === "backup_restore") {
    const prov = asStr(p.provider);
    return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
  }
  if (r.action === "save_local_copy") return { reply: say, action: { kind: "save_local_copy" } };
  if (r.action === "import_file") return { reply: say, action: { kind: "import_file" } };
  if (r.action === "save_to_cloud") {
    const ck = asStr(p.content_kind) ?? "";
    const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
    const dest = asStr(p.destination);
    const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
    return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
  }
  if (r.action === "remember") {
    const instruction = asStr(p.instruction);
    if (!instruction) return none(say || "What should I remember?");
    return { reply: say, action: { kind: "remember", instruction } };
  }

  // Everything else operates on the ACTIVE vehicle (mirrors routeMessage).
  if (!ctx.vehicleId) return none(NO_VEHICLE_HELP);
  const vehicleId = ctx.vehicleId;

  switch (r.action) {
    case "add_fillup": {
      const odometer = asNum(p.odometer);
      const volume = asNum(p.volume);
      if (odometer === null || volume === null) {
        return none(say || 'I need the odometer and the fuel amount — try "add fillup 42350 mi, 12.4 gal, $46.80".');
      }
      const isFullTank = asBool(p.is_full_tank);
      return { reply: say, action: { kind: "add_fillup", vehicle_id: vehicleId, odometer, volume, cost: asNum(p.cost) ?? undefined, is_full_tank: isFullTank ?? true } };
    }
    case "add_service": {
      const odometer = asNum(p.odometer);
      const kindName = asStr(p.kind_name);
      if (odometer === null || !kindName) {
        return none(say || 'I need what was done and the odometer — try "add service oil change at 42000 mi".');
      }
      return { reply: say, action: { kind: "add_service", vehicle_id: vehicleId, kind_name: kindName, odometer, cost: asNum(p.cost) ?? undefined } };
    }
    case "edit_fillup": {
      const target = asStr(p.target) ?? "last";
      const rows = await findFillup(vehicleId, target);
      if (rows.length === 0) return none(await fillupNotFound(vehicleId, target));
      if (rows.length > 1) return none(`I found ${rows.length} matching — be more specific:\n${pickFillupList(rows)}`);
      const f = rows[0];
      const patch: FillupPatch = {};
      const bits: string[] = [];
      const cost = asNum(p.cost); if (cost !== null) { patch.cost = cost; bits.push("cost"); }
      const odometer = asNum(p.odometer); if (odometer !== null) { patch.odometer = odometer; bits.push("odometer"); }
      const isFullTank = asBool(p.is_full_tank); if (isFullTank !== null) { patch.is_full_tank = isFullTank; bits.push(isFullTank ? "full" : "partial"); }
      if (bits.length === 0) return none(say || "What should I change on that fill-up — cost, odometer, or full/partial?");
      return { reply: say, action: { kind: "edit_fillup", id: f.id, patch, label: `the ${f.ts.slice(0, 10)} fill-up` } };
    }
    case "edit_service": {
      const target = asStr(p.target) ?? "last";
      const rows = await findService(vehicleId, target);
      if (rows.length === 0) return none(await serviceNotFound(vehicleId, target));
      if (rows.length > 1) return none(`I found ${rows.length} matching — be more specific:\n${pickServiceList(rows)}`);
      const s = rows[0];
      const patch: ServicePatch = {};
      const bits: string[] = [];
      const cost = asNum(p.cost); if (cost !== null) { patch.cost = cost; bits.push("cost"); }
      const odometer = asNum(p.odometer); if (odometer !== null) { patch.odometer = odometer; bits.push("odometer"); }
      const kindName = asStr(p.kind_name); if (kindName) { patch.kind = normalizeKind(kindName); bits.push("kind"); }
      if (bits.length === 0) return none(say || "What should I change on that service entry?");
      return { reply: say, action: { kind: "edit_service", id: s.id, patch, label: `the ${prettyKind(s.kind)} on ${s.ts.slice(0, 10)}` } };
    }
    case "delete_fillup": {
      const target = asStr(p.target) ?? "last";
      const rows = await findFillup(vehicleId, target);
      if (rows.length === 0) return none(await fillupNotFound(vehicleId, target));
      if (rows.length > 1) return none(`I found ${rows.length} matching — be more specific:\n${pickFillupList(rows)}`);
      const f = rows[0];
      return { reply: say, action: { kind: "delete_fillup", id: f.id, label: `the ${f.ts.slice(0, 10)} fill-up` } };
    }
    case "delete_service": {
      const target = asStr(p.target) ?? "last";
      const rows = await findService(vehicleId, target);
      if (rows.length === 0) return none(await serviceNotFound(vehicleId, target));
      if (rows.length > 1) return none(`I found ${rows.length} matching — be more specific:\n${pickServiceList(rows)}`);
      const s = rows[0];
      return { reply: say, action: { kind: "delete_service", id: s.id, label: `the ${prettyKind(s.kind)} on ${s.ts.slice(0, 10)}` } };
    }
    case "filter": {
      const entityRaw = asStr(p.entity)?.toLowerCase();
      const entity: "fillup" | "service" | "both" = entityRaw === "fillup" || entityRaw === "service" ? entityRaw : "both";
      return {
        reply: "",
        action: {
          kind: "filter", vehicle_id: vehicleId, entity,
          term: asStr(p.term), minCost: asNum(p.min_cost) ?? undefined, maxCost: asNum(p.max_cost) ?? undefined,
          label: say || undefined,
        },
      };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize", vehicle_id: vehicleId } };
    case "ask": {
      const question = asStr(p.question);
      if (!question) return none(say || "What do you want to know?");
      return { reply: "", action: { kind: "ask", vehicle_id: vehicleId, question } };
    }
    default:
      return none(say || HELP);
  }
}

// Vehicle reference → the local Vehicle. No target = the ACTIVE vehicle (or the only one);
// a named term fuzzy-matches across all vehicles (fleet standard). The model never assigns an id.
async function resolveVehicle(term: string | undefined, ctx: ChatContext): Promise<{ vehicle?: Vehicle; problem?: string }> {
  const all = await store.listVehicles();
  if (!all.length) return { problem: "You have no vehicles yet — add one first." };
  const q = term?.trim().replace(/[.?!]+$/, "");
  if (!q) {
    if (ctx.vehicleId) { const v = all.find((x) => x.id === ctx.vehicleId); if (v) return { vehicle: v }; }
    if (all.length === 1) return { vehicle: all[0] };
    return { problem: `Which vehicle? You have: ${all.map((v) => v.name).join(", ")}.` };
  }
  const matches = findNamedMatches(all, q);
  if (!matches.length) return { problem: notFoundMessage(all, q) };
  if (matches.length > 1) return { problem: `I found ${matches.length} vehicles matching "${q}" — be more specific: ${matches.map((v) => v.name).join(", ")}.` };
  return { vehicle: matches[0] };
}

export async function routeMessage(text: string, ctx: ChatContext): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 1) ADD VEHICLE — light client intent (no vehicle context needed).
  const vehM = t.match(/^(?:add|create|new)\s+(?:a\s+)?(?:vehicle|car)[:\-\s]+([\s\S]+)$/i);
  if (vehM) {
    const name = vehM[1].trim();
    if (!name) return { reply: "", action: { kind: "none", message: "What should the vehicle be called?" } };
    return { reply: "", action: { kind: "add_vehicle", name } };
  }

  // Everything else operates on the ACTIVE vehicle.
  if (!ctx.vehicleId) return { reply: "", action: { kind: "none", message: NO_VEHICLE_HELP } };
  const vehicleId = ctx.vehicleId;

  // 2) ADD FILL-UP.
  const fillM = t.match(/^(?:add|log|record)\s+(?:a\s+)?fill[\s-]?up[:\-\s]+([\s\S]+)$/i);
  if (fillM) {
    const payload = fillM[1].trim();
    const odometer = extractTagged(payload, ODO_TAGS) ?? extractUnitNumber(payload, ODO_UNITS);
    const volume = extractTagged(payload, VOL_TAGS) ?? extractUnitNumber(payload, VOL_UNITS);
    if (odometer === null || volume === null) {
      return { reply: "", action: { kind: "none", message: 'I need the odometer and the fuel amount — try "add fillup 42350 mi, 12.4 gal, $46.80".' } };
    }
    const cost = extractCost(payload) ?? undefined;
    const is_full_tank = /\bpartial\b/i.test(payload) ? false : true;
    return { reply: "", action: { kind: "add_fillup", vehicle_id: vehicleId, odometer, volume, cost, is_full_tank } };
  }

  // 3) ADD SERVICE.
  const svcM = t.match(/^(?:add|log|record)\s+(?:a\s+)?service[:\-\s]+([\s\S]+)$/i);
  if (svcM) {
    const payload = svcM[1].trim();
    const odometer = extractTagged(payload, ODO_TAGS) ?? extractUnitNumber(payload, ODO_UNITS);
    if (odometer === null) {
      return { reply: "", action: { kind: "none", message: 'I need what was done and the odometer — try "add service oil change at 42000 mi".' } };
    }
    let kindName: string | null = null;
    for (const k of SERVICE_KINDS) {
      const pretty = k.replace(/-/g, "\\s+");
      if (new RegExp(`\\b${pretty}\\b`, "i").test(payload)) { kindName = k; break; }
    }
    if (!kindName) {
      const m = payload.match(/^([a-zA-Z][a-zA-Z\s]*?)(?=\s*(?:\d|at\b|$))/);
      kindName = m && m[1].trim() ? m[1].trim() : null;
    }
    if (!kindName) {
      return { reply: "", action: { kind: "none", message: 'What was done? e.g. "add service brakes at 42000 mi".' } };
    }
    const cost = extractCost(payload) ?? undefined;
    return { reply: "", action: { kind: "add_service", vehicle_id: vehicleId, kind_name: kindName, odometer, cost } };
  }

  // 4) SUMMARIZE — the existing /narrate endpoint (billed, ONE call).
  if (/\b(summar(?:y|ise|ize)|overview|recap)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize", vehicle_id: vehicleId } };
  }

  // 5) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+)?(last|latest|recent|most\s+recent)?\s*(fill[\s-]?up|service)\b\s*(.*)$/i);
  if (delM) {
    const which = delM[2].toLowerCase().startsWith("fill") ? "fillup" : "service";
    const term = (delM[1] ?? delM[3] ?? "").trim();
    if (which === "fillup") {
      const rows = await findFillup(vehicleId, term);
      if (rows.length === 0) return { reply: "", action: { kind: "none", message: await fillupNotFound(vehicleId, term) } };
      if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching — be more specific:\n${pickFillupList(rows)}` } };
      const f = rows[0];
      return { reply: "", action: { kind: "delete_fillup", id: f.id, label: `the ${f.ts.slice(0, 10)} fill-up` } };
    }
    const rows = await findService(vehicleId, term);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await serviceNotFound(vehicleId, term) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching — be more specific:\n${pickServiceList(rows)}` } };
    const s = rows[0];
    return { reply: "", action: { kind: "delete_service", id: s.id, label: `the ${prettyKind(s.kind)} on ${s.ts.slice(0, 10)}` } };
  }

  // 6) EDIT — cost / odometer corrections on the last (or matched) fill-up/service.
  const edit = await routeEdit(t, vehicleId);
  if (edit) return edit;

  // 7) FILTER / SEARCH — local, free (no AI call). "show/find/list fillups|service …"
  const filterM = /\b(show|find|list|search|which)\b/i.test(low);
  if (filterM) {
    const wantsService = /\b(service|repair|oil|brake|tire|maintenance)/i.test(low);
    const wantsFillup = /\b(fill[\s-]?up|fuel|gas|refuel)/i.test(low);
    const entity: "fillup" | "service" | "both" = wantsService && !wantsFillup ? "service" : wantsFillup && !wantsService ? "fillup" : "both";
    const minM = t.match(/\b(?:over|above|more\s+than)\s+\$?([\d,.]+)/i);
    const maxM = t.match(/\b(?:under|below|less\s+than)\s+\$?([\d,.]+)/i);
    const minCost = minM ? parseNum(minM[1]) ?? undefined : undefined;
    const maxCost = maxM ? parseNum(maxM[1]) ?? undefined : undefined;
    return { reply: "", action: { kind: "filter", vehicle_id: vehicleId, entity, minCost, maxCost, label: t } };
  }

  // 8) ASK — question-shaped free text → the existing /ask endpoint (billed, ONE call). This is
  // the SAME "Ask your garage" feature the card already offers, just reachable by typing.
  if (/\?\s*$/.test(t) || /\b(how\s+much|how\s+many|when\s+did|what'?s\s+my|what\s+is\s+my)\b/i.test(low)) {
    return { reply: "", action: { kind: "ask", vehicle_id: vehicleId, question: t } };
  }

  // 9) Unclear → a short clarifying nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

// EDIT intents. Returns null if the message isn't an edit so the router can fall through.
async function routeEdit(t: string, vehicleId: string): Promise<One | null> {
  // "set/change the last fillup's cost to $X"
  let m = t.match(/^(?:set|change|update)\s+(?:the\s+)?(last|latest|recent)?\s*fill[\s-]?up(?:'s)?\s+cost\s+to\s+\$?([\d.,]+)/i);
  if (m) {
    const cents = parseNum(m[2]);
    if (cents === null) return null;
    const rows = await findFillup(vehicleId, m[1] ?? "last");
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: "I couldn't find a fill-up to update." } };
    const f = rows[0];
    return { reply: "", action: { kind: "edit_fillup", id: f.id, patch: { cost: cents }, label: `the ${f.ts.slice(0, 10)} fill-up` } };
  }
  // "set/change the last fillup's odometer to X"
  m = t.match(/^(?:set|change|update)\s+(?:the\s+)?(last|latest|recent)?\s*fill[\s-]?up(?:'s)?\s+odometer\s+to\s+([\d.,]+)/i);
  if (m) {
    const odo = parseNum(m[2]);
    if (odo === null) return null;
    const rows = await findFillup(vehicleId, m[1] ?? "last");
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: "I couldn't find a fill-up to update." } };
    const f = rows[0];
    return { reply: "", action: { kind: "edit_fillup", id: f.id, patch: { odometer: odo }, label: `the ${f.ts.slice(0, 10)} fill-up` } };
  }
  // "mark the last fillup as partial|full"
  m = t.match(/^mark\s+(?:the\s+)?(last|latest|recent)?\s*fill[\s-]?up\s+as\s+(full|partial)/i);
  if (m) {
    const rows = await findFillup(vehicleId, m[1] ?? "last");
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: "I couldn't find a fill-up to update." } };
    const f = rows[0];
    return { reply: "", action: { kind: "edit_fillup", id: f.id, patch: { is_full_tank: m[2].toLowerCase() === "full" }, label: `the ${f.ts.slice(0, 10)} fill-up` } };
  }
  // "set/change the last service's cost to $X"
  m = t.match(/^(?:set|change|update)\s+(?:the\s+)?(last|latest|recent)?\s*service(?:'s)?\s+cost\s+to\s+\$?([\d.,]+)/i);
  if (m) {
    const cents = parseNum(m[2]);
    if (cents === null) return null;
    const rows = await findService(vehicleId, m[1] ?? "last");
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: "I couldn't find a service entry to update." } };
    const s = rows[0];
    return { reply: "", action: { kind: "edit_service", id: s.id, patch: { cost: cents }, label: `the ${prettyKind(s.kind)} on ${s.ts.slice(0, 10)}` } };
  }
  return null;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
